import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Lock, LockOpen, Truck, MapPin, CheckCircle,
  XCircle, Clock, SkipForward, PencilSimple, Trash, WhatsappLogo,
} from "@phosphor-icons/react";
import RouteMap from "../../components/routes/RouteMap";

const STOP_STYLE = {
  pending: "bg-[#F9E4B7] text-[#7A5A11]",
  completed: "bg-[#2D5A27] text-white",
  skipped: "bg-[#F4C7B9] text-[#7A2E18]",
};

export default function RouteJobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [staff, setStaff] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [skipFor, setSkipFor] = useState(null);
  const [waOpen, setWaOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/routes/jobs/${id}`);
      setJob(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    load();
    api.get("/staff").then(r => setStaff(r.data || [])).catch(() => {});
    api.get("/vehicles").then(r => setVehicles(r.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  const del = async () => {
    if (!window.confirm("Delete this draft route job?")) return;
    try {
      await api.delete(`/routes/jobs/${id}`);
      toast.success("Deleted");
      nav("/operations/routes");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!job) return <AppShell title="Route Job"><div className="p-6 text-sm text-[#5C5A55]">Loading…</div></AppShell>;

  const statusStyle = {
    draft: "bg-[#E5E2D9] text-[#5C5A55]",
    assigned: "bg-[#BBDEFB] text-[#0D47A1]",
    in_progress: "bg-[#F9E4B7] text-[#7A5A11]",
    completed: "bg-[#2D5A27] text-white",
    cancelled: "bg-[#F4C7B9] text-[#7A2E18]",
  }[job.status] || "bg-[#E5E2D9]";

  return (
    <AppShell title={job.name || job.job_no}
      subtitle={`${job.stop_count} stops · ${job.distance_km?.toFixed?.(1)} km · ~${job.duration_min} min · ${job.engine}`}
      actions={
        <div className="flex gap-2">
          <button onClick={() => nav("/operations/routes")} className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm">
            <ArrowLeft size={14}/>Back
          </button>
          {job.status === "draft" && (
            <>
              <button onClick={del} data-testid="route-delete"
                      className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm text-[#C25D38]">
                <Trash size={14}/>Delete
              </button>
              <button onClick={() => setAssignOpen(true)} data-testid="route-assign-btn"
                      className="h-10 px-4 inline-flex items-center gap-2 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm">
                <Truck size={14}/>Assign & Lock
              </button>
            </>
          )}
          {(job.status === "assigned" || job.status === "in_progress" || job.status === "completed") && (
            <button onClick={() => setWaOpen(true)} data-testid="route-whatsapp-btn"
                    className="h-10 px-4 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-md text-sm">
              <WhatsappLogo size={14} weight="fill"/>Send WhatsApp
            </button>
          )}
          {(job.status === "assigned" || job.status === "in_progress") && (
            <button onClick={() => setReassignOpen(true)} data-testid="route-reassign-btn"
                    className="h-10 px-4 inline-flex items-center gap-2 bg-white border border-[#2D5A27] text-[#2D5A27] hover:bg-[#EFF3EE] rounded-md text-sm">
              <PencilSimple size={14}/>Reassign / Edit
            </button>
          )}
        </div>
      }>
      <div className="grid grid-cols-12 gap-5">
        {/* Left — summary */}
        <div className="col-span-12 lg:col-span-4 bg-white border border-[#E5E2D9] rounded-md p-5 self-start" data-testid="route-summary">
          <div className="flex items-center gap-2 mb-3">
            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${statusStyle}`}>
              {job.locked ? <Lock size={10} className="inline mr-1"/> : <LockOpen size={10} className="inline mr-1"/>}
              {job.status.replace("_", " ")}
            </span>
            <span className="font-mono text-xs text-[#5C5A55]">{job.job_no}</span>
          </div>
          <div className="space-y-2 text-sm">
            <KV k="Scheduled" v={job.scheduled_date}/>
            <KV k="Start" v={job.start?.label || `${job.start?.lat}, ${job.start?.lng}`}/>
            <KV k="End" v={job.end?.label || `${job.end?.lat}, ${job.end?.lng}`}/>
            <KV k="Engine" v={job.engine}/>
            <KV k="Distance" v={`${job.distance_km?.toFixed?.(1)} km`}/>
            <KV k="Duration" v={`~${job.duration_min} min`}/>
            <KV k="Staff" v={job.assigned_staff_name || "—"}/>
            <KV k="Vehicle" v={job.assigned_vehicle_no || "—"}/>
            <KV k="Stops" v={`${job.stops.filter(s => s.status === "completed").length} done · ${job.stops.filter(s => s.status === "skipped").length} skipped · ${job.stops.filter(s => s.status === "pending").length} pending`}/>
          </div>
          {job.last_reassign_reason && (
            <div className="mt-4 p-3 bg-[#FFF8E7] border border-[#F9E4B7] rounded-md text-xs text-[#7A5A11]">
              Last reassign: {job.last_reassign_reason}
            </div>
          )}
        </div>

        {/* Right — map + stops + events */}
        <div className="col-span-12 lg:col-span-8 space-y-5">
          <div className="bg-white border border-[#E5E2D9] rounded-md p-3" data-testid="route-job-map">
            <RouteMap route={job.stops} start={job.start} end={job.end} height="360px"/>
          </div>

          <div className="bg-white border border-[#E5E2D9] rounded-md p-5" data-testid="route-stops">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-3">Sequenced Stops</div>
            <ol className="space-y-2">
              {job.stops.map(s => (
                <li key={s.seq} data-testid={`stop-${s.seq}`}
                    className={`flex items-center gap-3 p-3 border border-[#F0ECE3] rounded-md ${s.status === "skipped" ? "opacity-60" : ""}`}>
                  <span className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center ${STOP_STYLE[s.status] || "bg-[#D4E0D1] text-[#2D5A27]"}`}>
                    {s.status === "completed" ? <CheckCircle size={14}/> : s.status === "skipped" ? <XCircle size={14}/> : s.seq}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{s.customer_name}</span>
                      <span className="font-mono text-[10px] text-[#8A8780]">{s.customer_code}</span>
                    </div>
                    <div className="text-xs text-[#5C5A55] truncate flex items-center gap-1">
                      <MapPin size={10}/>{s.address || `${s.lat}, ${s.lng}`}
                    </div>
                    {s.skip_reason && <div className="text-xs text-[#C25D38] mt-0.5">Skipped: {s.skip_reason}</div>}
                  </div>
                  <a href={s.nav_url} target="_blank" rel="noreferrer" data-testid={`nav-${s.seq}`}
                     className="text-[#2D5A27] text-xs hover:underline">navigate</a>
                  {job.locked && s.status === "pending" && (
                    <button onClick={() => setSkipFor(s)} data-testid={`skip-${s.seq}`}
                            className="text-[#C25D38] text-xs hover:underline inline-flex items-center gap-1">
                      <SkipForward size={12}/>skip
                    </button>
                  )}
                </li>
              ))}
            </ol>
          </div>

          {(job.events || []).length > 0 && (
            <div className="bg-white border border-[#E5E2D9] rounded-md p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#2D5A27] mb-3">Event Timeline</div>
              <ol className="space-y-2 text-sm">
                {job.events.map((e, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Clock size={10} className="mt-1 shrink-0 text-[#8A8780]"/>
                    <div className="flex-1">
                      <span className="font-medium">{e.type.replace("_", " ")}</span>
                      {e.reason && <span className="text-[#5C5A55]"> — {e.reason}</span>}
                      {e.seq && <span className="text-[#5C5A55]"> (stop #{e.seq})</span>}
                      <div className="text-xs text-[#8A8780]">{new Date(e.ts).toLocaleString()} · {e.actor_name || "system"}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>

      {assignOpen && (
        <AssignModal staff={staff} vehicles={vehicles} mode="assign"
                      onClose={() => setAssignOpen(false)}
                      onSubmit={async (body) => {
                        await api.post(`/routes/jobs/${id}/assign`, body);
                        toast.success("Assigned · route locked · tasks created");
                        setAssignOpen(false); load();
                      }}/>
      )}
      {reassignOpen && (
        <AssignModal staff={staff} vehicles={vehicles} mode="reassign"
                      current={{ assigned_staff_id: job.assigned_staff_id, assigned_vehicle_id: job.assigned_vehicle_id }}
                      onClose={() => setReassignOpen(false)}
                      onSubmit={async (body) => {
                        await api.post(`/routes/jobs/${id}/reassign`, body);
                        toast.success("Reassigned");
                        setReassignOpen(false); load();
                      }}/>
      )}
      {skipFor && (
        <SkipModal stop={skipFor} onClose={() => setSkipFor(null)}
                    onSubmit={async (reason) => {
                      await api.post(`/routes/jobs/${id}/stops/${skipFor.seq}/skip`, { reason });
                      toast.success("Stop skipped");
                      setSkipFor(null); load();
                    }}/>
      )}
      {waOpen && (
        <WhatsAppModal job={job} onClose={() => setWaOpen(false)}
                        onSubmit={async (message) => {
                          const r = await api.post(`/routes/jobs/${id}/whatsapp`,
                            { message: message || null, only_pending: true });
                          toast.success(`${r.data.sent.length} sent · ${r.data.skipped.length} skipped`);
                          setWaOpen(false); load();
                          // Auto-open the first 3 links so operator can hit Send in WhatsApp Web
                          (r.data.sent || []).slice(0, 3).forEach(s => window.open(s.url, "_blank"));
                          return r.data;
                        }}/>
      )}
    </AppShell>
  );
}

const KV = ({ k, v }) => (
  <div className="flex gap-2">
    <span className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] min-w-[80px]">{k}</span>
    <span className="flex-1 text-[#2D2B26]">{v}</span>
  </div>
);

const AssignModal = ({ staff, vehicles, mode, current = {}, onClose, onSubmit }) => {
  const [sid, setSid] = useState(current.assigned_staff_id || "");
  const [vid, setVid] = useState(current.assigned_vehicle_id || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!sid && mode === "assign") { toast.error("Staff is required"); return; }
    if (mode === "reassign" && reason.trim().length < 3) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      const body = { assigned_staff_id: sid || null, assigned_vehicle_id: vid || null };
      if (mode === "reassign") body.reason = reason;
      await onSubmit(body);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid={`${mode}-modal`}>
        <div className="font-heading text-lg mb-4">{mode === "assign" ? "Assign Staff & Vehicle" : "Reassign"}</div>
        <L>Staff {mode === "assign" && "*"}</L>
        <select value={sid} onChange={e => setSid(e.target.value)} data-testid={`${mode}-staff`}
                className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm mb-3">
          <option value="">— select staff —</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.name} · {s.phone}</option>)}
        </select>
        <L>Vehicle (optional)</L>
        <select value={vid} onChange={e => setVid(e.target.value)}
                className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm mb-3">
          <option value="">— none —</option>
          {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_no} · {v.type || "—"}</option>)}
        </select>
        {mode === "reassign" && (
          <>
            <L>Reason *</L>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} data-testid="reassign-reason"
                      className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm mb-3"/>
          </>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={go} disabled={busy} data-testid={`${mode}-submit`}
                  className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium">
            {busy ? "Saving…" : mode === "assign" ? "Assign & Lock" : "Reassign"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SkipModal = ({ stop, onClose, onSubmit }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (reason.trim().length < 3) { toast.error("Reason required"); return; }
    setBusy(true);
    try { await onSubmit(reason); } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="skip-modal">
        <div className="font-heading text-lg mb-2">Skip stop #{stop.seq}</div>
        <div className="text-sm text-[#5C5A55] mb-4">{stop.customer_name} · {stop.customer_code}</div>
        <L>Reason *</L>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} data-testid="skip-reason"
                  placeholder="e.g., Customer unavailable, no access, out of city..."
                  className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm mb-3"/>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={go} disabled={busy} data-testid="skip-submit"
                  className="h-10 px-4 bg-[#C25D38] hover:bg-[#A74A29] disabled:opacity-60 text-white rounded-md text-sm font-medium">
            {busy ? "Saving…" : "Skip stop"}
          </button>
        </div>
      </div>
    </div>
  );
};

const L = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{children}</label>
);

const WhatsAppModal = ({ job, onClose, onSubmit }) => {
  const defaultTpl = (
    "Dear {name},\n\n" +
    "Your waste pickup is scheduled. Keep your waste properly packed as per " +
    "CPCB 2026 instructions shared.\n\n" +
    `Date: ${job.scheduled_date || "—"}\n` +
    `Staff: ${job.assigned_staff_name || "—"}\n` +
    "Staff Phone: (auto-filled)\n\n" +
    "Please keep waste ready.\n\n" +
    "— Rescigo Health"
  );
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const eligible = (job.stops || []).filter(s => s.status === "pending" && s.customer_phone);
  const go = async () => {
    setBusy(true);
    try { await onSubmit(message); } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md max-w-lg w-full p-6" onClick={e => e.stopPropagation()} data-testid="whatsapp-modal">
        <div className="flex items-center gap-2 font-heading text-lg mb-2">
          <WhatsappLogo size={20} weight="fill" color="#25D366"/>
          Send WhatsApp to Customers
        </div>
        <div className="text-sm text-[#5C5A55] mb-3">
          {eligible.length} pending customer{eligible.length !== 1 ? "s" : ""} with phone.
          We will open the first 3 wa.me links; the rest are returned in a list you can batch-open.
        </div>
        <L>Custom message (optional — leave empty for default CPCB template)</L>
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={8}
                  placeholder={defaultTpl}
                  data-testid="whatsapp-message"
                  className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-xs font-mono mb-4"/>
        <div className="text-[11px] text-[#8A8780] mb-3">
          Placeholder <code>{"{name}"}</code> is replaced per-customer.
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={go} disabled={busy || eligible.length === 0}
                  data-testid="whatsapp-send"
                  className="h-10 px-4 bg-[#25D366] hover:bg-[#1DA851] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
            <WhatsappLogo size={14} weight="fill"/>{busy ? "Sending..." : `Send to ${eligible.length}`}
          </button>
        </div>
      </div>
    </div>
  );
};

